const RELOAD_KEY = "vite-preload-reloaded-at";
const RELOAD_GUARD_MS = 30_000;

export function initPreloadErrorHandler() {
	window.addEventListener("vite:preloadError", () => {
		const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
		if (Date.now() - last > RELOAD_GUARD_MS) {
			sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
			window.location.reload();
		}
	});
}

/**
 * Matches Vite code-split asset load failures — the CSS variant
 * ("Unable to preload CSS for …") plus the dynamic-import-module variants
 * browsers report ("Failed to fetch dynamically imported module",
 * "error loading dynamically imported module", "Importing a module script
 * failed"). These are almost always a user on a stale browser tab after a new
 * deploy: the old content-hashed chunk 404s. `initPreloadErrorHandler` above
 * already reloads the page to recover, so callers (e.g. the Sentry
 * `beforeSend` in `integrations/sentry/sentry.ts`) can drop them as expected,
 * self-healing noise. Unknown errors return `false` and are still reported.
 *
 * Recovery is NOT guaranteed by the reload alone: if the same import fails
 * again after the reload (e.g. mid-deploy edge propagation), this 30s guard
 * plus TanStack's own once-per-message reload in `lazyRouteComponent` are both
 * exhausted and the error surfaces in the router error boundary — that's what
 * `components/route-error.tsx` (defaultErrorComponent) is for. Dead /assets/*
 * URLs are a real 404 on Workers static assets (no SPA fallback for non-navigate
 * requests), so a stale chunk request fails cleanly instead of caching HTML.
 */
const PRELOAD_ERROR_RE =
	/Unable to preload (?:CSS|module)|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

export function isPreloadError(error: unknown): boolean {
	const message =
		error instanceof Error
			? error.message
			: typeof error === "string"
				? error
				: "";
	return PRELOAD_ERROR_RE.test(message);
}
